/**
 * WashMapView (web) — Leaflet + OpenStreetMap. Free, no API key; the native
 * twin (WashMapView.tsx) uses react-native-maps. Same props on both.
 *
 * Rendered as a real DOM <div> (react-native-web sits on react-dom, so
 * intrinsic elements work); Leaflet and its CSS load lazily so nothing
 * map-related ships in the initial chunk.
 */
import React, { useEffect, useRef } from 'react';

export interface WashPin {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
}

/** Geographic bounds of the visible map viewport. */
export interface MapViewport {
  north: number;
  south: number;
  east: number;
  west: number;
}

interface Props {
  pins: WashPin[];
  userLocation: { latitude: number; longitude: number } | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Brand accent for markers. */
  accent: string;
  height: number;
  /** Native-only (nested-scroll handoff); unused on web where the page
   *  doesn't fight the map for touches. */
  onInteractionChange?: (active: boolean) => void;
  /** Fires with the visible bounds after every pan/zoom settles (and after
   *  the initial fit). Never fires when the platform can't provide bounds —
   *  the host keeps its full list in that case. */
  onViewportChange?: (viewport: MapViewport) => void;
}

export default function WashMapView({ pins, userLocation, selectedId, onSelect, accent, height, onViewportChange }: Props) {
  const divRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<Map<string, any>>(new Map());
  const userMarkerRef = useRef<any>(null);
  const leafletRef = useRef<any>(null);
  // Latest syncMarkers — the async map-creation effect below runs whenever the
  // lazy leaflet import lands, which can be AFTER pins have already arrived;
  // calling the mount-render closure directly would sync an empty pin list
  // (and nothing re-syncs after). The ref always points at the fresh one.
  const syncRef = useRef<() => void>(() => {});
  // Latest onViewportChange — the moveend handler is bound once at map
  // creation, so it must read the current callback through a ref.
  const viewportCbRef = useRef(onViewportChange);
  viewportCbRef.current = onViewportChange;

  // Create the map once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!divRef.current || mapRef.current) return;
      // @ts-ignore — css import handled by Vite
      await import('leaflet/dist/leaflet.css');
      const L: any = await import('leaflet');
      if (cancelled || !divRef.current) return;
      leafletRef.current = L;
      const map = L.map(divRef.current, { zoomControl: true, attributionControl: true });
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }).addTo(map);
      map.setView([39.5, -92.5], 6); // sensible default until pins/user arrive
      // Report visible bounds whenever a pan/zoom settles (moveend also fires
      // after zoomend and after the initial fitBounds).
      map.on('moveend', () => {
        const b = map.getBounds();
        viewportCbRef.current?.({
          north: b.getNorth(), south: b.getSouth(), east: b.getEast(), west: b.getWest(),
        });
      });
      mapRef.current = map;
      syncRef.current();
    })();
    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
      markersRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncMarkers = () => {
    const L = leafletRef.current, map = mapRef.current;
    if (!L || !map) return;
    // Wash pins (circle markers avoid Leaflet's default-icon asset dance).
    markersRef.current.forEach(m => m.remove());
    markersRef.current.clear();
    for (const p of pins) {
      const marker = L.circleMarker([p.latitude, p.longitude], {
        radius: p.id === selectedId ? 11 : 8,
        color: '#ffffff',
        weight: 2,
        fillColor: accent,
        fillOpacity: p.id === selectedId ? 1 : 0.85,
      }).addTo(map);
      marker.bindTooltip(p.name);
      marker.on('click', () => onSelect(p.id));
      markersRef.current.set(p.id, marker);
    }
    // User dot.
    if (userMarkerRef.current) { userMarkerRef.current.remove(); userMarkerRef.current = null; }
    if (userLocation) {
      userMarkerRef.current = L.circleMarker([userLocation.latitude, userLocation.longitude], {
        radius: 6, color: '#ffffff', weight: 2, fillColor: '#1d6ef2', fillOpacity: 1,
      }).addTo(map).bindTooltip('You are here');
    }
    // Frame everything worth seeing.
    const points = [
      ...pins.map(p => [p.latitude, p.longitude] as [number, number]),
      ...(userLocation ? [[userLocation.latitude, userLocation.longitude] as [number, number]] : []),
    ];
    if (points.length > 0) map.fitBounds(L.latLngBounds(points).pad(0.2), { maxZoom: 13 });
  };

  syncRef.current = syncMarkers;

  // Re-sync markers when data changes (map itself persists).
  useEffect(() => { syncMarkers(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [pins, userLocation]);

  // Selection highlight without a full re-frame.
  useEffect(() => {
    const map = mapRef.current;
    markersRef.current.forEach((m, id) => m.setStyle({
      radius: id === selectedId ? 11 : 8,
      fillOpacity: id === selectedId ? 1 : 0.85,
    }));
    const sel = selectedId ? pins.find(p => p.id === selectedId) : null;
    if (map && sel) map.panTo([sel.latitude, sel.longitude]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  return <div ref={divRef} style={{ height, width: '100%', borderRadius: 12, overflow: 'hidden' }} />;
}
